import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * ProtectedRoute component for protecting routes that require authentication
 * and/or specific permissions.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render if authorized
 * @param {boolean} [props.requireAuth=true] - Require any authenticated user
 * @param {boolean} [props.requireStaff=false] - Require staff/admin user
 */
export default function ProtectedRoute({
  children,
  requireAuth = true,
  requireStaff = false,
}) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to="/auth/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check staff requirement
  if (requireStaff && !user?.is_admin) {
    return (
      <div
        className="min-h-screen flex items-center justify-center 
          bg-page p-4"
      >
        <div
          className="bg-surface rounded-lg shadow-xl p-8 max-w-md 
            w-full text-center"
        >
          <div
            className="w-16 h-16 mx-auto bg-warning bg-opacity-10 
              rounded-full flex items-center justify-center mb-4"
          >
            <svg
              className="w-8 h-8 text-warning"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 
                  1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 
                  16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-primary-content mb-2">
            Access Denied
          </h2>
          <p className="text-secondary-content mb-6">
            You don't have permission to access this page. This area is
            restricted to staff members only.
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full bg-primary text-white py-2 px-4 
              rounded-md hover:bg-primary-hover transition-colors 
              font-medium"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // User is authorized, render children
  return <>{children}</>;
}
